import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Upload, FileText, Trash2, Check, X, Loader2, Sparkles, Edit2 } from 'lucide-react';

const ComptabiliteFactures = ({ 
  postesDepenses, 
  factures, 
  selectedYear, 
  coproId, 
  apiClient, 
  getErrorMessage, 
  onRefresh
}) => {
  const [showForm, setShowForm] = useState(false);
  const [showAnalyse, setShowAnalyse] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingFacture, setEditingFacture] = useState(null);
  
  const [formData, setFormData] = useState({
    description: '', 
    fournisseur: '',
    montant: '', 
    date: new Date().toISOString().split('T')[0], 
    poste_id: '', 
    fichier_base64: null,
    fichier_nom: null
  });

  const getPosteFactures = (posteId) => factures.filter(f => f.poste_id === posteId);
  const getPosteTotal = (posteId) => getPosteFactures(posteId).reduce((s, f) => s + parseFloat(f.montant || 0), 0);
  const totalFactures = factures.reduce((s, f) => s + parseFloat(f.montant || 0), 0);

  const resetForm = () => {
    setFormData({ description: '', fournisseur: '', montant: '', date: new Date().toISOString().split('T')[0], poste_id: '', fichier_base64: null, fichier_nom: null });
    setEditingFacture(null);
    setShowForm(false);
    setShowAnalyse(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }
    
    setAnalysing(true);
    setShowAnalyse(true);
    
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      
      const response = await apiClient.post('/coproprietes/' + coproId + '/comptabilite/factures/analyser', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const analyse = response.data.analyse || {};
      
      setFormData({
        description: analyse.description || '',
        fournisseur: analyse.fournisseur || '',
        montant: analyse.montant_ttc?.toString() || '',
        date: analyse.date_facture || new Date().toISOString().split('T')[0],
        poste_id: analyse.poste_id_suggere || '',
        fichier_base64: response.data.fichier_base64,
        fichier_nom: response.data.fichier_nom
      });
      
      setShowForm(true);
      toast.success('Facture analysée par l\'IA !');
      
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'analyse'));
      setShowAnalyse(false);
    } finally {
      setAnalysing(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.description || !formData.montant || !formData.poste_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('description', formData.description);
      fd.append('fournisseur', formData.fournisseur);
      fd.append('montant', formData.montant);
      fd.append('date', formData.date);
      fd.append('poste_id', formData.poste_id);
      fd.append('annee', selectedYear);
      if (formData.fichier_base64) {
        fd.append('fichier_base64', formData.fichier_base64);
        fd.append('fichier_nom', formData.fichier_nom);
      }

      await apiClient.post('/coproprietes/' + coproId + '/comptabilite/factures', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Facture ajoutée et archivée dans le coffre-fort');
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (factureId) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try {
      await apiClient.delete('/coproprietes/' + coproId + '/comptabilite/factures/' + factureId);
      toast.success('Facture supprimée');
      onRefresh();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    }
  };

  const getPosteName = (posteId) => {
    const p = postesDepenses.find(post => post.id === posteId);
    return p ? p.nom : 'Inconnu';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Factures par poste</h2>
          <p className="text-sm text-slate-500">Total : {totalFactures.toFixed(2)} € sur {selectedYear}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => { resetForm(); setShowForm(true); }} variant="outline">
            + Saisie manuelle
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept="application/pdf" onChange={handleFileUpload} className="hidden" />
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg cursor-pointer">
              <Sparkles className="w-4 h-4" />
              Import avec IA
            </span>
          </label>
        </div>
      </div>

      {/* Analyse en cours */}
      {showAnalyse && analysing && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="font-semibold text-blue-900">Analyse IA en cours...</p>
          <p className="text-sm text-blue-700">Extraction des informations de la facture</p>
        </div>
      )}

      {/* Formulaire */}
      {showForm && !analysing && (
        <div className="bg-slate-50 rounded-xl p-6 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">
              {formData.fichier_base64 ? (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                  Facture pré-remplie par l'IA
                </span>
              ) : editingFacture ? 'Modifier la facture' : 'Nouvelle facture'}
            </h3>
            {formData.fichier_nom && (
              <span className="text-sm text-slate-500 flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {formData.fichier_nom}
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input 
              type="text" 
              placeholder="Fournisseur" 
              value={formData.fournisseur}
              onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
              className="px-3 py-2 border rounded-lg" 
            />
            <input 
              type="number" 
              step="0.01" 
              placeholder="Montant TTC € *" 
              value={formData.montant}
              onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
              className="px-3 py-2 border rounded-lg" 
            />
            <input 
              type="date" 
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="px-3 py-2 border rounded-lg" 
            />
            <select 
              value={formData.poste_id} 
              onChange={(e) => setFormData({ ...formData, poste_id: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">Poste de dépenses *</option>
              {postesDepenses.map(p => (
                <option key={p.id} value={p.id}>{p.nom}</option>
              ))}
            </select>
            <input 
              type="text" 
              placeholder="Description *" 
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="px-3 py-2 border rounded-lg md:col-span-2" 
            />
          </div>
          <div className="flex gap-3 mt-4">
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
            <Button variant="outline" onClick={resetForm}>
              <X className="w-4 h-4 mr-2" />Annuler
            </Button>
          </div>
          {formData.fichier_base64 && (
            <p className="text-xs text-emerald-600 mt-3 flex items-center gap-1">
              <Check className="w-3 h-3" />
              Le PDF sera automatiquement archivé dans le coffre-fort Documents
            </p>
          )}
        </div>
      )}

      {/* Liste des factures par poste */}
      {postesDepenses.length === 0 ? (
        <div className="text-center py-10 bg-slate-50 rounded-xl border">
          <p className="text-slate-500">Aucun poste de dépenses configuré</p>
        </div>
      ) : (
        postesDepenses.map(poste => (
          <div key={poste.id} className="bg-white rounded-xl border overflow-hidden">
            <div className="flex items-center justify-between p-4 bg-slate-50 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold">{poste.nom}</h3>
                  <p className="text-sm text-slate-500">{getPosteFactures(poste.id).length} facture(s)</p>
                </div>
              </div>
              <p className="text-lg font-bold text-red-600">-{getPosteTotal(poste.id).toFixed(2)} €</p>
            </div>
            {getPosteFactures(poste.id).length > 0 ? (
              <div className="divide-y">
                {getPosteFactures(poste.id).map(f => (
                  <div key={f.id} className="flex items-center justify-between p-4 hover:bg-slate-50 group">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{f.description}</p>
                        {f.fichier_nom && (
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">PDF</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">
                        {f.fournisseur && <span>{f.fournisseur} • </span>}
                        {f.date ? new Date(f.date).toLocaleDateString('fr-FR') : 'Date inconnue'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-semibold text-red-600">-{parseFloat(f.montant).toFixed(2)} €</p>
                      <button 
                        onClick={() => handleDelete(f.id)} 
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="p-4 text-slate-500 text-center">Aucune facture</p>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default ComptabiliteFactures;
